/**
 * Loan-officer verification panel (P1.B.3).
 *
 * Shown on a PENDING_VERIFICATION loan to a user holding
 * `loan.approve_verification`. The officer ticks the checklist, picks an
 * outcome, and submits to the dedicated `POST /loans/:id/verification`
 * endpoint — NOT the generic transitions path, which no longer accepts
 * verification outcomes.
 *
 * Validation mirrors the server domain (`validateVerificationDecision`):
 * APPROVED needs every checklist item ticked; REJECTED / REVISION_REQUESTED
 * need a ≥10-char note. The server is authoritative; this is just early UX.
 *
 * The checklist itself is fetched from the server so it can never drift from
 * the domain's canonical list.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ApiError,
  type Loan,
  type LoanVerification,
  type LoanVerificationOutcome,
  type LoanVerificationResult,
  type SubmitLoanVerificationRequest,
  type VerificationChecklistItem,
} from "@dfp/api-client";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { api } from "../lib/api.js";

const OUTCOMES: readonly { value: LoanVerificationOutcome; label: string }[] = [
  { value: "APPROVED", label: "Approve" },
  { value: "REVISION_REQUESTED", label: "Request revision" },
  { value: "REJECTED", label: "Reject" },
];

const MIN_NOTES = 10;

export interface VerificationPanelProps {
  readonly loanId: string;
  /** Called with the updated loan after a decision lands. */
  readonly onDecided: (loan: Loan) => void;
}

export function VerificationPanel({ loanId, onDecided }: VerificationPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [outcome, setOutcome] = useState<LoanVerificationOutcome>("APPROVED");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const checklistQuery = useQuery({
    queryKey: ["loans", "verification-checklist"],
    queryFn: () =>
      api.get<readonly VerificationChecklistItem[]>("/api/loans/verification-checklist"),
    staleTime: Infinity,
  });

  const decide = useMutation({
    mutationFn: (body: SubmitLoanVerificationRequest) =>
      api.post<LoanVerificationResult>(`/api/loans/${loanId}/verification`, body),
    onSuccess: (result) => {
      setChecked({});
      setNotes("");
      setOutcome("APPROVED");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["loans", "verification", loanId] });
      onDecided(result.loan);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Decision rejected");
      } else {
        setError("Network error");
      }
    },
  });

  const template = checklistQuery.data ?? [];
  const allChecked = template.length > 0 && template.every((i) => checked[i.key]);
  const notesOk = notes.trim().length >= MIN_NOTES;
  const canSubmit = outcome === "APPROVED" ? allChecked : notesOk;

  function submit(): void {
    setError(null);
    if (outcome === "APPROVED" && !allChecked) {
      setError("Tick every checklist item before approving.");
      return;
    }
    if (outcome !== "APPROVED" && !notesOk) {
      setError(`A note of at least ${MIN_NOTES} characters is required for this outcome.`);
      return;
    }
    const body: SubmitLoanVerificationRequest = {
      outcome,
      checklist: template.map((i) => ({ key: i.key, checked: Boolean(checked[i.key]) })),
      ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
    };
    decide.mutate(body);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification decision</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        {checklistQuery.isError ? (
          <p className="text-sm text-red-700">Failed to load the checklist.</p>
        ) : (
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Checklist {outcome === "APPROVED" ? "(all required to approve)" : "(evidence)"}
            </legend>
            {template.map((item) => (
              <label key={item.key} className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(checked[item.key])}
                  onChange={(e) =>
                    setChecked((prev) => ({ ...prev, [item.key]: e.target.checked }))
                  }
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span>{item.label}</span>
              </label>
            ))}
          </fieldset>
        )}

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Outcome
          </span>
          <select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as LoanVerificationOutcome)}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Notes {outcome === "APPROVED" ? "(optional)" : `(required, ≥ ${MIN_NOTES} chars)`}
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <Button
          variant={outcome === "REJECTED" ? "danger" : "primary"}
          onClick={submit}
          disabled={decide.isPending || !canSubmit}
        >
          {decide.isPending ? "Submitting…" : `Submit — ${labelFor(outcome)}`}
        </Button>

        <VerificationHistory loanId={loanId} />
      </CardBody>
    </Card>
  );
}

function VerificationHistory({ loanId }: { loanId: string }): JSX.Element | null {
  const historyQuery = useQuery({
    queryKey: ["loans", "verification", loanId],
    queryFn: () => api.get<readonly LoanVerification[]>(`/api/loans/${loanId}/verification`),
  });

  const history = historyQuery.data ?? [];
  if (history.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-slate-200 pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Previous decisions
      </p>
      {history.map((v) => (
        <div key={v.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
          <div className="flex items-center justify-between">
            <Badge tone={toneFor(v.outcome)}>{v.outcome}</Badge>
            <span className="text-slate-500">{new Date(v.decidedAt).toLocaleString()}</span>
          </div>
          {v.notes ? <p className="mt-1 text-slate-700">{v.notes}</p> : null}
          <p className="mt-1 text-slate-500">
            {v.checklist.filter((c) => c.checked).length}/{v.checklist.length} checklist items
            ticked
          </p>
        </div>
      ))}
    </div>
  );
}

function labelFor(outcome: LoanVerificationOutcome): string {
  return OUTCOMES.find((o) => o.value === outcome)?.label ?? outcome;
}

function toneFor(outcome: LoanVerificationOutcome): "success" | "danger" | "neutral" {
  if (outcome === "APPROVED") return "success";
  if (outcome === "REJECTED") return "danger";
  return "neutral";
}
